import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';

// D2D = Door-to-Door Sales (bulk vehicle/fleet sales)
const EMPTY = { sale_date: today(), vehicle_no:'', driver_name:'', customer_id:'', customer_name:'Cash', product_code:'HSD', qty:'', rate:'', delivery_address:'' };

export default function D2DSales() {
  const { t } = useTranslation();
  const { data: customers } = useApi(() => api.customers.list(), []);
  const { data: products  } = useApi(() => api.products.list(),  []);
  const { data: sales, loading, error, reload } = useApi(() => api.sales.list(), []);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm]   = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]     = useState('');
  const [filter, setFilter] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(()=>setMsg(''), 3500); };

  // D2D records are sales with vehicle_no or delivery_address in note (locally tracked)
  const [d2dRecords, setD2dRecords] = useState([]);

  const handleSave = async () => {
    if (!form.qty || !form.rate)    { notify('❌ ' + t('d2dSales.errors.qtyRateRequired')); return; }
    if (!form.vehicle_no)           { notify('❌ ' + t('d2dSales.errors.vehicleRequired')); return; }
    setSaving(true);
    try {
      const res = await api.sales.create({
        sale_date:     form.sale_date,
        customer_id:   form.customer_id || null,
        customer_name: form.customer_name || 'Cash',
        product_code:  form.product_code,
        qty:           parseFloat(form.qty),
        rate:          parseFloat(form.rate),
      });
      // Store D2D-specific details locally
      setD2dRecords(prev => [...prev, {
        ...res,
        sale_date:        form.sale_date,
        bill_no:          res.bill_no,
        vehicle_no:       form.vehicle_no,
        driver_name:      form.driver_name,
        customer_name:    form.customer_name || 'Cash',
        product_code:     form.product_code,
        qty:              parseFloat(form.qty),
        rate:             parseFloat(form.rate),
        amount:           parseFloat(form.qty) * parseFloat(form.rate),
        delivery_address: form.delivery_address,
      }]);
      notify('✅ ' + t('d2dSales.toasts.saved')); setShowForm(false); setForm(EMPTY); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const filtered = d2dRecords.filter(s =>
    !filter ||
    s.vehicle_no?.toLowerCase().includes(filter.toLowerCase()) ||
    s.customer_name?.toLowerCase().includes(filter.toLowerCase()) ||
    s.driver_name?.toLowerCase().includes(filter.toLowerCase())
  );
  const total = filtered.reduce((s,r) => s + Number(r.amount||0), 0);

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <div className="page-header">
        <h2 className="page-title">{t('d2dSales.title')} <span style={{fontSize:13,fontWeight:400,color:'var(--text-muted)'}}>{t('d2dSales.subtitle')}</span></h2>
        <div style={{display:'flex',gap:8}}>
          <input className="form-control" style={{width:200}} placeholder={t('d2dSales.searchPlaceholder')}
            value={filter} onChange={e=>setFilter(e.target.value)}/>
          <button className="btn btn-primary btn-sm" onClick={()=>setShowForm(!showForm)}>＋ {t('d2dSales.newSale')}</button>
          <button className="export-btn">⬆ {t('common.export')}</button>
        </div>
      </div>

      {msg && <div className="notify-bar">{msg}</div>}

      {showForm && (
        <div className="section-card">
          <div className="section-header">{t('d2dSales.newEntryHeader')}</div>
          <div className="form-row">
            <div className="form-group"><label>{t('common.date')}</label>
              <input type="date" className="form-control" value={form.sale_date}
                onChange={e=>setForm({...form,sale_date:e.target.value})}/>
            </div>
            <div className="form-group"><label>{t('d2dSales.vehicleNo')} *</label>
              <input className="form-control" value={form.vehicle_no}
                onChange={e=>setForm({...form,vehicle_no:e.target.value.toUpperCase()})}
                placeholder="TS09AB1234"/>
            </div>
            <div className="form-group"><label>{t('d2dSales.driverName')}</label>
              <input className="form-control" value={form.driver_name}
                onChange={e=>setForm({...form,driver_name:e.target.value})} placeholder={t('d2dSales.driverPlaceholder')}/>
            </div>
            <div className="form-group"><label>{t('common.customer')}</label>
              <select className="form-control" value={form.customer_id} onChange={e=>{
                const c=(customers||[]).find(x=>String(x.id)===e.target.value);
                setForm({...form,customer_id:e.target.value,customer_name:c?c.name:'Cash'});
              }}>
                <option value="">{t('common.cash')}</option>
                {(customers||[]).filter(c=>c.type==='Credit').map(c=>(
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group"><label>{t('common.product')}</label>
              <select className="form-control" value={form.product_code} onChange={e=>{
                const p=(products||[]).find(x=>x.code===e.target.value);
                setForm({...form,product_code:e.target.value,rate:p?p.rate:''});
              }}>
                {(products||[]).map(p=><option key={p.id} value={p.code}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{t('d2dSales.quantityL')}</label>
              <input type="number" className="form-control" value={form.qty}
                onChange={e=>setForm({...form,qty:e.target.value})} placeholder="0"/>
            </div>
            <div className="form-group"><label>{t('common.rate')} (₹)</label>
              <input type="number" className="form-control" value={form.rate}
                onChange={e=>setForm({...form,rate:e.target.value})} placeholder="0.00"/>
            </div>
            {form.qty && form.rate && (
              <div className="form-group balance-group">
                <label>{t('common.amount')}</label>
                <div className="balance-value" style={{color:'var(--secondary)',background:'#f0fff4',borderColor:'#9ae6b4'}}>
                  ₹{fmtNum(parseFloat(form.qty||0)*parseFloat(form.rate||0))}
                </div>
              </div>
            )}
          </div>
          <div className="form-row" style={{paddingTop:0}}>
            <div className="form-group" style={{flex:2}}><label>{t('d2dSales.deliveryAddress')}</label>
              <input className="form-control" value={form.delivery_address}
                onChange={e=>setForm({...form,delivery_address:e.target.value})} placeholder={t('d2dSales.deliveryPlaceholder')}/>
            </div>
          </div>
          <div className="action-bar" style={{padding:'0 16px 14px'}}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?t('common.saving'):`💾 ${t('common.save')}`}</button>
            <button className="btn btn-secondary" onClick={()=>setShowForm(false)}>✕ {t('common.cancel')}</button>
          </div>
        </div>
      )}

      {loading && <Spinner />}
      {error   && <ErrBanner msg={error} />}

      <div className="section-card">
        <div className="section-header">
          {t('d2dSales.recordsSummary', { n: filtered.length })} | {t('common.total')}: ₹{fmtNum(total)}
        </div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead><tr>
              <th style={{textAlign:'left'}}>#</th>
              <th style={{textAlign:'left'}}>{t('common.date')}</th>
              <th style={{textAlign:'left'}}>{t('d2dSales.billNo')}</th>
              <th style={{textAlign:'left'}}>{t('d2dSales.vehicleNo')}</th>
              <th style={{textAlign:'left'}}>{t('d2dSales.driver')}</th>
              <th style={{textAlign:'left'}}>{t('common.customer')}</th>
              <th style={{textAlign:'left'}}>{t('common.product')}</th>
              <th>{t('d2dSales.qtyL')}</th>
              <th>{t('common.rate')} (₹)</th>
              <th>{t('common.amount')} (₹)</th>
              <th style={{textAlign:'left'}}>{t('d2dSales.deliveryAddress')}</th>
            </tr></thead>
            <tbody>
              {filtered.map((s,i)=>(
                <tr key={i}>
                  <td>{i+1}</td>
                  <td>{s.sale_date}</td>
                  <td style={{fontWeight:700,color:'var(--primary)',fontFamily:'monospace'}}>{s.bill_no}</td>
                  <td><span className="badge badge-primary">{s.vehicle_no}</span></td>
                  <td>{s.driver_name||'—'}</td>
                  <td>{s.customer_name}</td>
                  <td><span className={`badge ${s.product_code==='MS'?'badge-danger':s.product_code==='CNG'?'badge-success':'badge-info'}`}>{s.product_code}</span></td>
                  <td className="right-align">{s.qty}</td>
                  <td className="right-align">₹{fmtNum(s.rate)}</td>
                  <td className="right-align">₹{fmtNum(s.amount)}</td>
                  <td style={{fontSize:11.5,color:'var(--text-muted)'}}>{s.delivery_address||'—'}</td>
                </tr>
              ))}
              {filtered.length===0 && (
                <tr><td colSpan={11} style={{textAlign:'center',padding:20,color:'var(--text-muted)'}}>
                  {t('d2dSales.emptyState')}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
